import { formatDataToLabelValue, Option } from '@repo/ui';
import { GetAllPersonProfileResponse } from 'domain/models';
import { useCallback, useMemo, useState } from 'react';

import { getValidProfilesToFilters } from '../../utils/filters/get-valid-profiles-to-filters';

interface EntityAndCourseFiltersByProfilesHookProps {
  profiles: GetAllPersonProfileResponse;
  courses: OptionsAPI[];
  organizations: OptionsAPI[];
}

type OptionsAPI = {
  id: string;
  name: string;
};

/**
 * Hook responsável por gerenciar os filtros de Organização e Curso com base nos perfis válidos de uma pessoa.
 *
 * Esse hook foi criado para encapsular a lógica de seleção e auto-preenchimento dos filtros utilizados no componente
 * `EntityAndCourseFiltersByProfiles`. Ele determina automaticamente os valores iniciais com base nas regras de negócio:
 *
 * (Apenas perfis válidos são considerados para exibição dos filtros)
 *
 * * 1. **Vários perfis válidos e várias Organizações/Cursos**:
 *    - Exibe os dois selects com placeholders: "Todas as Organizações" e "Todos os Cursos".
 *
 * 2. **Apenas um perfil válido com uma Organização/Curso**:
 *    - Os campos são preenchidos automaticamente com a única Organização e Curso disponíveis.
 *
 * 3. **Vários perfis e uma Organização, mas vários Cursos**:
 *    - Autopreenche a Organização e exibe o campo de Curso com placeholder "Todos os Cursos".
 *
 * 4. **Vários perfis e uma Organização/Curso apenas**:
 *    - Ambos os campos são preenchidos automaticamente.
 *
 * Além disso, fornece os valores selecionados e os callbacks para atualizar os filtros dinamicamente.
 *
 * @param {EntityAndCourseFiltersByProfilesHookProps} params
 * @prop {GetAllPersonProfileResponse} profiles - Lista de perfis da pessoa com vínculos a entidades e cursos.
 * @prop {Array<{ id: string; name: string }>} courses - Cursos vinculados à pessoa (via API `admin/person/courses/personHashId`).
 * @prop {Array<{ id: string; name: string }>} organizations - Entidades vinculadas à pessoa (via API `admin/entity/?personHashId=`).
 *
 * @returns {{
 *   orgOptions: Option[];
 *   courseOptions: Option[];
 *   selectedCourse: Option | null;
 *   selectedOrganization: Option | null;
 *   onCourseChange: (course: Option | null) => void;
 *   onOrganizationChange: (organization: Option | null) => void;
 *   hasValidProfile: boolean;
 * }}
 *
 * @example
 * ```tsx
 * const {
 *   orgOptions,
 *   courseOptions,
 *   selectedCourse,
 *   selectedOrganization,
 *   onCourseChange,
 *   onOrganizationChange,
 *   hasValidProfile,
 * } = useEntityAndCourseFiltersByProfiles({ profiles, courses, organizations });
 * ```
 */

// Esta função é pura e não depende de props ou estados — foi movida para fora do hook
// para evitar que seja recriada em toda renderização, otimizando o desempenho.
const getAutofillBehaviorRulesToFilters = (
  orgOptions: Option[],
  courseOptions: Option[],
): { organization: Option | null; course: Option | null } => {
  const orgOnly = orgOptions.length === 1;
  const courseOnly = courseOptions.length === 1;

  return {
    organization: orgOnly ? orgOptions[0] : null,
    course: courseOnly ? courseOptions[0] : null,
  };
};

export const useEntityAndCourseFiltersByProfiles = ({
  profiles,
  courses,
  organizations,
}: EntityAndCourseFiltersByProfilesHookProps) => {
  const orgOptions = useMemo(
    () => formatDataToLabelValue(organizations as never, 'id', 'name'),
    [organizations],
  );

  const courseOptions = useMemo(
    () => formatDataToLabelValue(courses as never, 'id', 'name'),
    [courses],
  );

  const { organization, course } = useMemo(
    () => getAutofillBehaviorRulesToFilters(orgOptions, courseOptions),
    [orgOptions, courseOptions],
  );

  const [selectedCourse, setSelectedCourse] = useState<Option | null>(course);
  const [selectedOrganization, setSelectedOrganization] =
    useState<Option | null>(organization);

  const validProfiles = getValidProfilesToFilters(profiles);
  const hasValidProfile = validProfiles?.length > 0;

  const onCourseChange = useCallback((course: Option | null) => {
    setSelectedCourse(course);
  }, []);

  const onOrganizationChange = useCallback((organization: Option | null) => {
    setSelectedOrganization(organization);
  }, []);

  return {
    orgOptions,
    courseOptions,
    onCourseChange,
    selectedCourse,
    hasValidProfile,
    onOrganizationChange,
    selectedOrganization,
  };
};
